import { useState, useEffect } from 'react';
import { Box, Typography, Chip, CircularProgress, Alert } from '@mui/material';
import { supabase } from '../../../services/supabase';
import { StoryCell } from './RecSimulation';

interface InterestProfileVector {
  status: 'active' | 'probation';
  strength: number;
  effectiveStrength: number;
  createdFrom: 'seeding' | 'behavior';
  lastReinforcedAt: string;
  interactionCount: number;
  clusterLabel: string | null;
  clusterMatchScore: number | null;
  nearestStories: Array<{ slug: string; showSlug: string; similarity: number }>;
}

interface InterestProfileResult {
  vectors: InterestProfileVector[];
  specificity: number | null;
}

function relativeTime(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffDays = Math.floor(diffMs / 86_400_000);
  if (diffDays < 1) return 'today';
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
}

const VectorCard = ({ vector, index }: { vector: InterestProfileVector; index: number }) => {
  const isProbation = vector.status === 'probation';
  const lowConfidence = (vector.clusterMatchScore ?? 0) < 0.5;

  return (
    <Box
      sx={{
        p: 1.5,
        mb: 1.5,
        border: '1px solid',
        borderColor: isProbation ? 'divider' : 'primary.main',
        borderStyle: isProbation ? 'dashed' : 'solid',
        borderRadius: 1,
        opacity: isProbation ? 0.75 : 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2">
          Vector {index + 1}
          {vector.clusterLabel && (
            <>
              {' — '}
              <Box
                component="span"
                sx={{ color: lowConfidence ? 'warning.main' : 'text.primary' }}
              >
                &ldquo;{vector.clusterLabel}&rdquo; ({vector.clusterMatchScore})
              </Box>
            </>
          )}
        </Typography>
        <Chip size="small" label={vector.status} color={isProbation ? 'default' : 'primary'} variant="outlined" />
        <Chip size="small" label={vector.createdFrom} variant="outlined" />
      </Box>
      <Typography variant="caption" color="text.secondary" display="block">
        strength {vector.strength} (effective {vector.effectiveStrength}) — {vector.interactionCount} interactions — last reinforced {relativeTime(vector.lastReinforcedAt)}
      </Typography>
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        {vector.nearestStories.length === 0 ? (
          <Typography variant="caption" color="text.secondary">No nearby stories found.</Typography>
        ) : (
          vector.nearestStories.map(s => (
            <Box key={s.slug} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StoryCell slug={s.slug} showName={s.showSlug} />
              <Typography variant="caption" color="text.secondary">{s.similarity}</Typography>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export const UserInterestsTab = ({ userId }: { userId: string }) => {
  const [result, setResult] = useState<InterestProfileResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setResult(null);
      setError(null);
      try {
        const { data, error: fnError } = await supabase.functions.invoke(
          'get-recommendations',
          { body: { userId, lang: 'en', mode: 'interests' } }
        );
        if (cancelled) return;
        if (fnError) throw new Error(fnError.message);
        if (!data?.success) throw new Error(data?.error ?? 'Query failed');
        setResult(data.result);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">Loading interest vectors...</Typography>
      </Box>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!result || result.vectors.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        No interest vectors yet — user hasn&apos;t seeded or interacted enough to build a profile.
      </Typography>
    );
  }

  const sorted = [...result.vectors].sort((a, b) => (a.status === b.status ? 0 : a.status === 'active' ? -1 : 1));

  return (
    <Box>
      {result.specificity !== null && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Seeding specificity: {result.specificity}
        </Typography>
      )}
      {sorted.map((v, i) => (
        <VectorCard key={i} vector={v} index={i} />
      ))}
    </Box>
  );
};
